import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';

// Email validation regex - matches backend validation
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const EmailMeModal = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  mode = 'email', // 'email' | 'lead'
  title,
  subtitle,
  submitLabel,
  loadingLabel,
  privacyText,
  showCompanyName = true,
  requireName = false,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    companyName: '',
    email: ''
  });

  const [errors, setErrors] = useState({});
  const [isEmailValid, setIsEmailValid] = useState(false);

  const resolvedTitle = title || (mode === 'lead' ? 'Before You Download' : 'Email me my resume');
  const resolvedSubtitle = subtitle || (mode === 'lead'
    ? 'Enter your details to download your formatted resume'
    : "We'll send your formatted resume as a PDF to your inbox.");
  const resolvedSubmitLabel = submitLabel || (mode === 'lead' ? 'Continue to Download' : 'Send my resume');
  const resolvedLoadingLabel = loadingLabel || (mode === 'lead' ? 'Saving details...' : 'Sending...');
  const resolvedPrivacyText = privacyText || (mode === 'lead'
    ? 'By submitting, you agree to receive occasional product updates. We respect your privacy.'
    : "We'll only use your email to send your resume. No spam, ever.");

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({ name: '', companyName: '', email: '' });
      setErrors({});
      setIsEmailValid(false);
    }
  }, [isOpen]);

  // Validate email in real-time
  useEffect(() => {
    const email = formData.email.trim();
    if (email) {
      const isValid = EMAIL_REGEX.test(email);
      setIsEmailValid(isValid);

      if (!isValid && email.length > 3) {
        setErrors(prev => ({ ...prev, email: 'Please enter a valid email address' }));
      } else {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.email;
          return newErrors;
        });
      }
    } else {
      setIsEmailValid(false);
    }
  }, [formData.email]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const nextErrors = {};

    if (requireName && !formData.name.trim()) {
      nextErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      nextErrors.email = 'Email is required';
    } else if (!isEmailValid) {
      nextErrors.email = 'Please enter a valid email address';
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    onSubmit({
      name: formData.name.trim(),
      companyName: formData.companyName.trim(),
      email: formData.email.trim()
    });
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={handleBackdropClick}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 20 }}
            transition={{ type: 'spring', duration: 0.3 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-rose-600 to-rose-700 px-6 py-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">{resolvedTitle}</h2>
                  <p className="text-rose-100 text-sm">{resolvedSubtitle}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                disabled={isLoading}
                className="text-white/80 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              {/* Name Field */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                  Name {requireName ? <span className="text-rose-600">*</span> : <span className="text-slate-400 text-xs">(Optional)</span>}
                </label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  disabled={isLoading}
                  placeholder="John Doe"
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed text-slate-900 placeholder:text-slate-400 ${errors.name ? 'border-red-300 bg-red-50' : 'border-slate-200'}`}
                />
                {errors.name && (
                  <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.name}
                  </p>
                )}
              </div>

              {/* Company Name Field (Optional) */}
              {showCompanyName && (
                <div>
                  <label htmlFor="companyName" className="block text-sm font-medium text-slate-700 mb-2">
                    Company Name <span className="text-slate-400 text-xs">(Optional)</span>
                  </label>
                  <input
                    type="text"
                    id="companyName"
                    value={formData.companyName}
                    onChange={(e) => handleInputChange('companyName', e.target.value)}
                    disabled={isLoading}
                    placeholder="Acme Corporation"
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed text-slate-900 placeholder:text-slate-400"
                  />
                </div>
              )}

              {/* Email Field (Required) */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                  Email Address <span className="text-rose-600">*</span>
                </label>
                <div className="relative">
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    disabled={isLoading}
                    placeholder="john.doe@example.com"
                    required
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed text-slate-900 placeholder:text-slate-400 ${
                      errors.email ? 'border-red-300 bg-red-50' : 'border-slate-200'
                    } ${
                      isEmailValid && formData.email ? 'border-green-300 bg-green-50' : ''
                    }`}
                  />
                  {isEmailValid && formData.email && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                  )}
                  {errors.email && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    </div>
                  )}
                </div>
                {errors.email && (
                  <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.email}
                  </p>
                )}
              </div>


              {/* Submit Button */}
              <Button
                type="submit"
                disabled={!isEmailValid || isLoading || (requireName && !formData.name.trim())}
                className="w-full bg-rose-600 hover:bg-rose-700 text-white py-3 text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    {resolvedLoadingLabel}
                  </>
                ) : (
                  <>
                    <Mail className="w-5 h-5 mr-2" />
                    {resolvedSubmitLabel}
                  </>
                )}
              </Button>

              {/* Privacy Notice */}
              <p className="text-xs text-center text-slate-500 mt-4">
                {resolvedPrivacyText}
              </p>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default EmailMeModal;
